// Set the fee · Revise the fee · Fee history — on any instruction, instruction
// 1 included (v4.20.0, Karan 28/10/2026, 8451: "I now need to give them a
// revised fee"). The project page's instruction row menu opens these.
//
// Set the fee CREATES the fee record on an instruction that has none, so it is
// a MODAL (Dialogs & Forms §1), like Record fee before it. Revise the fee adds
// a revision — a new record, the old fee kept — so it is a modal too; the
// figure behind it is the one being replaced. Fee history reads the record and
// writes nothing: a view panel.
//
// Every write is the server's (setInstructionFee, functions/instruction-fee.js):
// it keeps the history on the instruction and the line in the project's
// "Changes to this record". Invoices already issued are never touched.
import { escapeHtml, safeUrl, toastV4 } from '../ui.js';
import { openDialog, openPanel } from './dialog.js';
import { setInstructionFee } from '../db.js';
import { BASIS_OPTIONS, feeProposalDocs, feeLogRows, setFeePrefill } from '../instruction-fee-model.js';

const gbp = (n) => `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const fmtWhen = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''; };
const isHttp = (v) => /^https?:\/\//i.test(String(v || '').trim());

function docFields(docs, { label, preselect = true }) {
  const out = [];
  if (docs.length) {
    out.push({ key: 'documentId', label, type: 'select', span: true, optional: true, value: preselect && docs[0].issued ? docs[0].id : '',
      options: [{ value: '', label: 'None' }, ...docs.map((d) => ({ value: d.id, label: `${d.name}${d.revision ? ` · rev ${d.revision}` : ''}${d.issued ? ' · issued' : ' · draft'}` }))] });
  }
  out.push({ key: 'docUrl', label: docs.length ? 'Or a link to it' : `${label}: a link`, type: 'text', span: true, optional: true,
    placeholder: 'https://drive.google.com/…', validate: (v) => (!v || isHttp(v) ? null : 'A link starts with https://') });
  return out;
}

/** Set the fee on an instruction with no fee. ctx: { project, projectId, instr, instructions, documents, enquiry, enquiryNos, onDone } */
export function openSetFee(ctx) {
  const { project = {}, projectId, instr = {}, instructions = [], documents = [], enquiry = null, enquiryNos = [], onDone } = ctx;
  const seq = Number(instr.seq) || 1;
  const pre = setFeePrefill({ project, instructions, instr, enquiry });
  const docs = feeProposalDocs(documents, [project.number, ...enquiryNos]);
  openDialog({
    eyebrow: `Instruction ${seq}`,
    title: 'Set the fee',
    subtitle: `${project.number || ''} · ${instr.description || 'Original scope'}`,
    width: 520,
    note: pre.why || 'The agreed fee for this instruction, ex VAT.',
    fields: [
      { key: 'fee', label: 'Agreed fee, ex VAT', type: 'money', affix: '£', value: pre.fee, required: true, placeholder: 'e.g. 8500',
        validate: (v) => (Number(v) > 0 ? null : 'Enter the agreed fee.') },
      { key: 'basis', label: 'Based on', type: 'radio', span: true, value: pre.basis, options: BASIS_OPTIONS },
      ...docFields(docs, { label: 'The fee proposal or email' }),
      { key: 'note', label: 'Note', type: 'textarea', span: true, optional: true, max: 600, placeholder: 'e.g. As the proposal of March 2025' },
    ],
    primary: {
      label: 'Set the fee',
      onSubmit: async (v) => {
        await setInstructionFee({ projectId, seq, mode: 'set', fee: Number(v.fee), basis: v.basis || 'fee-proposal',
          documentId: v.documentId || '', docUrl: v.documentId ? '' : (v.docUrl || ''), note: v.note || '' });
        toastV4(`Instruction ${seq}: fee set`);
        if (onDone) onDone();
      },
    },
  });
}

/** Revise the fee: the previous fee kept, the reason and the revised proposal recorded. */
export function openReviseFee(ctx) {
  const { project = {}, projectId, instr = {}, documents = [], enquiryNos = [], invoicedNet = 0, onDone } = ctx;
  const seq = Number(instr.seq) || 1;
  const cur = Number(instr.fee) || 0;
  const docs = feeProposalDocs(documents, [project.number, ...enquiryNos]);
  openDialog({
    eyebrow: `Instruction ${seq} · now ${gbp(cur)}`,
    title: 'Revise the fee',
    subtitle: `${project.number || ''} · ${instr.description || 'Original scope'}`,
    width: 520,
    note: invoicedNet > 0
      ? `${gbp(invoicedNet)} is already invoiced on it. Those invoices keep their figures; the next one shows the revised fee and what is left.`
      : 'The previous fee stays in the history.',
    fields: [
      { key: 'fee', label: 'Revised fee, ex VAT', type: 'money', affix: '£', value: '', required: true, placeholder: String(Math.round(cur)),
        validate: (v) => (!(Number(v) > 0) ? 'Enter the revised fee.' : Number(v) === cur ? `It is already ${gbp(cur)}.` : null) },
      { key: 'reason', label: 'Why it is revised', type: 'textarea', span: true, required: true, max: 600, placeholder: 'e.g. Scope grew: second-floor extension added' },
      ...docFields(docs, { label: 'The revised fee proposal', preselect: false }),
    ],
    primary: {
      label: 'Revise the fee',
      onSubmit: async (v) => {
        if (!String(v.reason || '').trim()) throw new Error('Say why the fee is being revised.');
        await setInstructionFee({ projectId, seq, mode: 'revise', fee: Number(v.fee), reason: v.reason, basis: 'fee-proposal',
          documentId: v.documentId || '', docUrl: v.documentId ? '' : (v.docUrl || '') });
        toastV4(`Instruction ${seq}: fee revised`);
        if (onDone) onDone();
      },
    },
  });
}

/** The instruction's fee history (reads only). */
export function openFeeHistory({ project = {}, instr = {} }) {
  const seq = Number(instr.seq) || 1;
  const rows = feeLogRows(instr.feeLog);
  openPanel({
    eyebrow: `${project.number || ''} · Instruction ${seq}`,
    title: 'Fee history',
    subtitle: Number(instr.fee) > 0 ? `Agreed fee now ${gbp(instr.fee)}${Number(instr.feeRevisions) > 0 ? `, revised ${instr.feeRevisions} time${Number(instr.feeRevisions) === 1 ? '' : 's'}` : ''}` : 'No fee yet',
    view: true,
    render(body) {
      body.innerHTML = rows.length ? `<ol class="ifee-log">${rows.map((r) => `<li class="ifee-log-row">
          <div class="ifee-log-top"><b>${escapeHtml(r.title)}</b><span class="ifee-log-chg">${escapeHtml(r.change)}</span></div>
          <div class="ifee-log-meta">${escapeHtml([fmtWhen(r.when), r.who, r.basis].filter(Boolean).join(' · '))}</div>
          ${r.why ? `<div class="ifee-log-why">${escapeHtml(r.why)}</div>` : ''}
          ${r.source ? `<div class="ifee-log-meta">${escapeHtml(r.source)}</div>` : ''}
          ${r.doc ? (r.doc.url && safeUrl(r.doc.url) !== '#' ? `<a class="ifee-log-doc" href="${escapeHtml(safeUrl(r.doc.url))}" target="_blank" rel="noopener">${escapeHtml(r.doc.name)}</a>` : `<span class="ifee-log-meta">${escapeHtml(r.doc.name)}</span>`) : ''}
        </li>`).join('')}</ol>`
        : `<p class="ifee-log-none">${Number(instr.fee) > 0 ? 'This fee was recorded before Track kept a history of fees.' : 'No fee has been recorded on this instruction.'}</p>`;
    },
  });
}
